import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { useResponsive } from 'src/hooks/useResponsive';
import { ApplicationState } from 'store/store';
import { BottomDialog, IBottomDialogProps } from './BottomDialog';
import { isEmptyString } from './typeInference';

const PREFIX = 'MerchantBrandedDialog';

const classes = {
    locationTitle: `${PREFIX}locationTitle`,
    locationImg: `${PREFIX}locationImg`,
    bagIconWrapper: `${PREFIX}bagIconWrapper`,
    icon: `${PREFIX}icon`,
    boldText: `${PREFIX}boldText`,
    selectBox: `${PREFIX}selectBox`,
    selectBoxList: `${PREFIX}selectBoxList`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'hasImg'
})<{ hasImg: boolean }>(({ theme, hasImg }) => ({
    [`& .${classes.locationTitle}`]: {
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        textAlign: 'center',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.locationImg}`]: {
        width: '100%',
        objectFit: 'cover',
        height: '200px'
    },

    [`& .${classes.bagIconWrapper}`]: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: hasImg ? theme.spacing(-50 / 8) : 0,
        position: 'relative',
        zIndex: 1
    },

    [`& .${classes.icon}`]: {
        fontSize: theme.spacing(7),
        stroke: theme.palette.background.default,
        color: theme.palette.background.default,
        lineHeight: 0
    },

    [`& .${classes.boldText}`]: {
        fontWeight: theme.typography.fontWeightBold
    },

    [`& .${classes.selectBox}`]: {
        maxHeight: '200px',
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.selectBoxList}`]: {
        padding: theme.spacing(0)
    }
}));

interface IProps extends IBottomDialogProps {
    icon: JSX.Element | null;
    title: string;
    subtitle: string;
    open: boolean;
    onClose: () => void;
    hideImage?: boolean;
    hideTitle?: boolean;
    isLoading?: boolean;
}

export const MerchantBrandedDialog: React.FC<React.PropsWithChildren<React.PropsWithChildren<IProps>>> = ({
    title,
    subtitle,
    icon,
    open,
    onClose,
    children,
    hideImage,
    hideTitle,
    ...rest
}) => {
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);

    const { isDesktop } = useResponsive();

    const renderLocalisationStrings = React.useCallback(
        (string: string) => (
            <Typography
                key={string}
                align="center"
                variant="body1"
                sx={
                    isEmptyString(string)
                        ? {
                              mb: 2
                          }
                        : undefined
                }
            >
                {string}
            </Typography>
        ),
        []
    );

    return (
        <BottomDialog {...rest} open={open} onClose={onClose}>
            <StyledBox
                hasImg={!!currentLocation?.mainImageUrl}
                paddingX={1}
                minWidth={isDesktop && !currentLocation?.mainImageUrl ? 445 : 0}
            >
                {!hideImage && (
                    <>
                        <Box paddingY={1}>
                            <Typography className={classes.locationTitle} variant="h6">
                                {currentLocation?.title}
                            </Typography>
                        </Box>
                        <Box marginX={-2}>
                            {currentLocation?.mainImageUrl && (
                                <img
                                    className={classes.locationImg}
                                    src={currentLocation?.mainImageUrl}
                                    alt={currentLocation?.title}
                                />
                            )}
                            {!!icon && (
                                <div className={classes.bagIconWrapper}>
                                    <span className={classes.icon}>{icon}</span>
                                </div>
                            )}
                        </Box>
                    </>
                )}

                <Box marginTop={1}>
                    {!hideTitle && (
                        <Typography align="center" variant="body1">
                            <b>{title}</b>
                        </Typography>
                    )}

                    <Box display="flex" justifyContent="center">
                        {subtitle.split('\n').map(renderLocalisationStrings)}
                    </Box>
                </Box>
                {children}
            </StyledBox>
        </BottomDialog>
    );
};
